import Notifications, { notify as notifyApi } from '@kyvg/vue3-notification';
import type { App } from 'vue';
import type { NotificationsOptions } from '@kyvg/vue3-notification';

export enum NotifyType {
    Info = 'info',
    Success = 'success',
    Warning = 'warning',
    Error = 'error',
}

type NotifyParams = string | NotificationsOptions;

type NotifyWrapper = typeof notifyApi & {
    trigger: (type: NotifyType, params?: NotifyParams) => void;
    info: (params?: NotifyParams) => void;
    success: (params?: NotifyParams) => void;
    error: (params?: NotifyParams) => void;
    warning: (params?: NotifyParams) => void;
    clean: () => void;
};

const trigger = (type: NotifyType, params?: NotifyParams) => {
    let normalizedParams: NotifyParams | undefined = params;

    if (typeof normalizedParams === 'string') {
        normalizedParams = { title: '', text: normalizedParams };
    }

    if ([NotifyType.Warning, NotifyType.Error].includes(type)) {
        if (normalizedParams && typeof normalizedParams === 'object') {
            normalizedParams.ignoreDuplicates = true;
        }
    }

    if (normalizedParams && typeof normalizedParams === 'object') {
        const customParams: NotificationsOptions & {
            type: NotifyType;
            data: Record<string, unknown>;
        } = {
            type,
            data: {},
        };

        switch (type) {
            case NotifyType.Success:
                customParams.data.icon = 'bold/check-1';
                break;
            case NotifyType.Warning:
                customParams.data.icon = 'bold/alert-triangle';
                break;
            case NotifyType.Error:
                customParams.data.icon = 'bold/close';
                break;
            case NotifyType.Info:
                customParams.data.icon = 'regular/information-circle';
                break;
            default:
                break;
        }

        notifyApi({ ...customParams, ...normalizedParams });
    }
};

const notify = Object.assign(
    (params?: NotifyParams) => {
        if (params != null) {
            notifyApi(params);
        }
    },
    {
        trigger,
        info: (params?: NotifyParams) => trigger(NotifyType.Info, params),
        success: (params?: NotifyParams) => trigger(NotifyType.Success, params),
        error: (params?: NotifyParams) => trigger(NotifyType.Error, params),
        warning: (params?: NotifyParams) => trigger(NotifyType.Warning, params),
        clean: () => notifyApi({ clean: true }),
        close: (id: string | number) => notifyApi.close(id),
    }
) as NotifyWrapper;

export default {
    install(app: App) {
        // Install Vue 3 notifications plugin
        app.use(Notifications, {
            name: 'notifications',
            componentName: 'vue-notifications',
        });

        // Add our custom wrapper
        app.config.globalProperties.$notify = notify;

        // Provide via provide/inject for the Composition API
        app.provide('notify', notify);
    },
};

export { notify };
